import React from 'react';
import App from 'next/app';
import Head from 'next/head';
import getConfig from 'next/config';
import { ApolloProvider } from 'react-apollo';
import { createGlobalStyle } from 'styled-components';
import Cookies from 'universal-cookie';
import AuthenticationProvider from '@g4/next-furniture/Authentication/AuthenticationProvider';
import TSAccountProtectionProvider from '@g4/next-furniture/TSAccountProtectionProvider/TSAccountProtectionProvider';
import tracking from '@g4/g4-analytics';

import { ThemeProvider, theme } from '../components/Boneyard';
import ErrorBoundary from './app/ErrorBoundary';
import TrackingProvider from './app/TrackingProvider';
import MaxymiserProvider from '../components/MaxymiserProvider';
import ModalProvider from '../components/ModalProvider';
import { withApolloClient } from '../graphql';
import AllegiantStadiumBannerProvider from './app/AllegiantStadiumBannerProvider';
import { UserContextProvider } from '@g4/next-furniture/UserContextProvider';
import OneTrustCookie from '@g4/next-furniture/OneTrust/OneTrustCookie';
import JsonLdSchema from '../components/JsonLdSchema';

const nextConfig = getConfig();
const wwwCookieDomain =
  nextConfig.publicRuntimeConfig.wwwCookieDomain || '.allegiantair.com';

const GlobalStyles = createGlobalStyle`
  html {
    overflow-x: hidden;
  }

  html,
  body,
  #__next {
    height: 100%;
  }

  * {
    :focus:not(:focus-visible) {
      outline: none;
    }
  }

  body {
    font-family: 'Montserrat', sans-serif;
  }
`;

class MyApp extends App {
  componentDidMount() {
    const buildVersion = getConfig().publicRuntimeConfig.buildVersion;
    const appVersion = getConfig()?.publicRuntimeConfig?.appVersion;
    const cookies = new Cookies(document && document.cookie);
    const params = new URL(document.location).searchParams;
    const promo = params.get('promo');
    const cid = cookies.cookies['_ga'];
    const isAuthenticated = cookies.cookies[' js_auth_token'];

    // Common GA4 data layer variables for booking path
    tracking.setDataLayerVars({
      customer_type: isAuthenticated ? 'Customer' : 'Guest',
      customer_id: cid,
      drupal_version: buildVersion,
      search_manifest: cookies.cookies['transaction-id'],
      content_group: 'HomePage',
      loyalty_points_total: 0,
      loyalty_points_redeemed: 0,
      qualified_user: 0,
      software_version: appVersion,
      affiliation: 'WWW - Home',
    });

    if (promo) {
      cookies.set('voucher_promo_code', promo, {
        path: '/',
        domain: wwwCookieDomain,
        secure: true,
      });
    }
  }

  render() {
    const { Component, pageProps, apolloClient } = this.props;

    return (
      <>
        <OneTrustCookie
          oneTrustToken={nextConfig.publicRuntimeConfig.oneTrustToken}
        />
        <JsonLdSchema />
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, minimum-scale=1"
          />
          <link
            type="text/css"
            rel="stylesheet"
            href="/public/fonts/fonts.css"
          />
        </Head>
        <GlobalStyles />
        {/*
          TODO second errorBoundary that does not rely on apollo provider (or alternate strategy)
        */}
        <ApolloProvider client={apolloClient}>
          <ErrorBoundary>
            <TSAccountProtectionProvider>
              <MaxymiserProvider>
                <TrackingProvider>
                  <UserContextProvider>
                    <AuthenticationProvider>
                      <AllegiantStadiumBannerProvider>
                        <ThemeProvider theme={theme}>
                          <ModalProvider>
                            <Component {...pageProps} />
                          </ModalProvider>
                        </ThemeProvider>
                      </AllegiantStadiumBannerProvider>
                    </AuthenticationProvider>
                  </UserContextProvider>
                </TrackingProvider>
              </MaxymiserProvider>
            </TSAccountProtectionProvider>
          </ErrorBoundary>
        </ApolloProvider>
      </>
    );
  }
}

export default withApolloClient({ App: MyApp });
